<template>

    <div class="ui-input" :class="resolvedClasses" :style="$attrs.style">

        <div v-if="hasPrefix" class="prefix">
            <slot name="prefix"><span>{{ prefix }}</span></slot>
        </div>

        <div v-if="hasBefore" class="before">
            <slot name="before"></slot>
        </div>

        <div class="icon" v-if="icon || uiIcon || $slots.icon">
            <i :class="icon">
                <slot name="icon"></slot>
                <ui-icon v-if="uiIcon" :icon="uiIcon"/>
            </i>
        </div>

        <input
            autocomplete="off" :type="type" :name="name"
            v-bind="passedAttrs"
            @focus="focussed = true" @blur="focussed = false" @change="onChange"
            :disabled="disabled" :class="{ clickable }"
            ref="inputRef"/>

        <div v-if="hasAfter" class="after">
            <slot name="after"></slot>
        </div>

        <div v-if="hasSuffix" class="suffix">
            <slot name="suffix"><span>{{ suffix }}</span></slot>
        </div>

    </div>

</template>

<script lang="ts">

import { Component, Prop, Ref, Vue, Watch } from 'vue-facing-decorator';

import { isArray, isNaN, isNil, isObject, omit } from 'lodash';
import Cleave from 'cleave.js';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin } from '@wittignl/js-core';

import Icon from '../ui/Icon.vue';

const REGEX_TIME = /^(0[0-9]|1[0-9]|2[0-3]|[0-9]):[0-5][0-9]$/;

@Component({
    name: 'ui-input',
    inheritAttrs: false,
    components: {
        'ui-icon': Icon
    },
    emits: ['update:modelValue', 'change']
})
export default class Input extends Vue {

    @Prop({ type: [String, Number] })
    modelValue: string | number;

    @Prop({ type: String })
    name: string;

    @Prop({ type: [String, Boolean, Array] })
    error: string | boolean | string[];

    @Prop({ type: Boolean })
    clickable: boolean;

    @Prop({ type: Boolean })
    disabled: boolean;

    @Prop({ type: String, default: 'text' })
    type: string;

    @Prop({ type: String })
    prefix: string;

    @Prop({ type: String })
    suffix: string;

    @Prop({ type: String })
    icon: string;

    @Prop({ type: String })
    uiIcon: string;

    @Prop({ type: Boolean })
    decimal: boolean;

    @Prop({ type: Boolean })
    time: boolean;

    @Prop({ type: Boolean })
    year: boolean;

    @Prop({ type: Boolean })
    number: boolean;

    @Prop({ type: [String, Object] })
    cleave: 'time' | 'year' | 'number' | 'decimal' | Cleave;

    @Prop({ type: Boolean })
    focus: boolean;

    @Prop({ type: [Number, String] })
    min: number|string;

    @Prop({ type: [Number, String] })
    max: number|string;

    @Prop({ type: [Number, String] })
    maxLength: number|string;

    @Ref
    inputRef: HTMLInputElement;

    focussed = false;
    cleaveInstance: Cleave;

    mounted(){

        if(this.cleaveOptions){
            //@ts-ignore
            this.cleaveInstance = new Cleave(this.inputRef, this.cleaveOptions);
        }

        this.inputRef.addEventListener('input', this.onInput);
        this.updateFromValue(this.modelValue);

        if(this.focus){
            this.$nextTick(() => this.focusInput());
        }
    }

    @Watch('modelValue')
    onModelValueUpdate(value) {

        this.updateFromValue(value);
    }

    beforeUnmount(){

        if(this.cleaveInstance){
            this.cleaveInstance.destroy();
        }
    }

    getElement(){

        return this.inputRef;
    }

    onChange(e){

        e.stopPropagation();
        this.$emit('change', this.modelValue, e);
    }

    get resolvedClasses(){

        const classes = {
            prefix: this.hasPrefix, suffix: this.hasSuffix,
            before: this.hasBefore, after: this.hasAfter,
            error: this.hasError, focussed: this.focussed,
            disabled: this.disabled,
            'icon-left': !!this.icon,
            'has-content': !isNil(this.modelValue) && this.modelValue !== ''
        };

        const attrClass = this.$attrs.class;
        let passedClasses = [];

        if(attrClass){
            passedClasses = isArray(attrClass) ? attrClass : [attrClass];
        }

        return [classes, ...passedClasses];
    }

    focusInput(){

        this.inputRef?.focus();
        this.focussed = true;
    }

    blurInput(){

        this.inputRef?.blur();
        this.focussed = false;
    }

    onInput(e){

        const rawValue = e.target.value;

        if(rawValue !== null){

            const valueString = rawValue.toString();

            if(this.maxLength){

                const maxLength = parseInt(this.maxLength as string);

                if(valueString.length > maxLength){
                    this.updateFromValue(valueString.slice(0, maxLength));
                }
            }

            if(this.min){

                const min = parseInt(this.min as string);
                const valueDecimal = parseFloat(rawValue);

                if(valueDecimal < min){
                    this.updateFromValue(min);
                }
            }

            if(this.max){

                const max = parseInt(this.max as string);
                const valueDecimal = parseFloat(rawValue);

                if(valueDecimal > max){
                    this.updateFromValue(max);
                }
            }
        }

        this.$emit('update:modelValue', this.getOutput(), e);
    }

    updateFromValue(input){

        if(!this.inputRef){
            return;
        }

        if(this.cleaveInstance){
            this.cleaveInstance.setRawValue(input);
        }
        else if(this.inputRef) {
            this.inputRef.value = input !== undefined ? input : null;
        }
    }

    getOutput(){

        const output = this.inputRef?.value;

        if(output === null || output === ''){
            return null;
        }

        if(this.cleaveInstance){

            if(this.cleaveOptions.numeral){

                const number = parseFloat(this.cleaveInstance.getRawValue());

                if(isNaN(number)){
                    return null;
                }

                return number;
            }
            else if(this.cleaveOptions.date){

                if(this.year){

                    let year = parseInt(this.cleaveInstance.getRawValue());
                    return !isNaN(year) ? year : null;
                }

                return this.cleaveInstance.getISOFormatDate() || null;
            }
            else if(this.cleaveOptions.time){

                return REGEX_TIME.test(output) ? output : null;
            }
        }

        return output;
    }

    get isNumeric(): boolean {
        return this.cleaveOptions?.numeral || this.year;
    }

    get hasPrefix(): boolean {
        return !!this.prefix || !!this.$slots.prefix;
    }

    get hasSuffix(): boolean {
        return !!this.suffix || !!this.$slots.suffix;
    }

    get hasBefore(): boolean {
        return !!this.$slots.before;
    }

    get hasAfter(): boolean {
        return !!this.$slots.after;
    }

    get hasError(): boolean {
        return isArray(this.error) ? this.error.length > 0 : !!this.error;
    }

    get cleaveOptions(): any {

        let cleave = this.cleave;

        if(this.time){
            cleave = 'time';
        }
        else if(this.year){
            cleave = 'year';
        }
        if(this.number){
            cleave = 'number';
        }
        else if(this.decimal){
            cleave = 'decimal';
        }

        if(!cleave || isObject(cleave)){
            return cleave;
        }

        switch(cleave){

            case 'time':

                return {
                    time: true,
                    timePattern: ['h', 'm']
                };

            case 'year':

                return {
                    date: true,
                    datePattern: ['Y']
                };

            case 'number':

                return {
                    numeral: true,
                    numeralDecimalScale: 0,
                    numeralThousandsGroupStyle: 'thousand',
                    numeralDecimalMark: this.$t('core.formatting.number.decimalSeparator'),
                    delimiter: this.$t('core.formatting.number.thousandSeparator')
                };

            case 'decimal':

                return {
                    numeral: true,
                    numeralDecimalScale: 2,
                    numeralThousandsGroupStyle: 'thousand',
                    numeralDecimalMark: this.$t('core.formatting.number.decimalSeparator'),
                    delimiter: this.$t('core.formatting.number.thousandSeparator')
                };
        }

        return null;
    }

    get passedAttrs(){

        return omit(this.$attrs, ['class', 'style', 'onUpdate:modelValue']);
    }
}

export type InputComp = InstanceType<typeof Input>
export type InputProps = ComponentProps<InputComp>
export const inputCompMixin = createComponentMixin(Input)

</script>
<style src="../../../scss/component/control/Input.scss" lang="scss"></style>